/**
 * Authentication Context
 *
 * Manages user authentication state and permissions for the backoffice.
 * - Handles Google OAuth login/logout
 * - Fetches user permissions from backend
 * - Provides permission checking helpers (hasPageAccess, isAdmin)
 * - Auto-registers new users on first login
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import { googleLogout } from "@react-oauth/google";
import { jwtDecode } from "jwt-decode";
import { UserPermissions } from "../types/admin";
import { mainApi } from "../services/api";

/**
 * Google JWT payload structure
 */
interface GoogleUser {
  sub: string;
  name: string;
  email: string;
  picture?: string;
  given_name?: string;
  family_name?: string;
}

/**
 * Auth context value interface
 */
interface AuthContextType {
  /** Current logged in user info from Google */
  user: GoogleUser | null;
  /** User permissions from backend */
  permissions: UserPermissions | null;
  /** True while fetching permissions */
  isLoading: boolean;
  /** True if user has admin role */
  isAdmin: boolean;
  /** Check if user can access a specific page path */
  hasPageAccess: (path: string) => boolean;
  /** Check if user has a specific role */
  hasRole: (role: string) => boolean;
  /** Handle login with Google credential */
  login: (credential: string) => Promise<void>;
  /** Handle logout */
  logout: () => void;
  /** Manually refresh permissions from backend */
  refreshPermissions: () => Promise<void>;
  /** Error message if login failed */
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Local storage keys
 */
const STORAGE_KEYS = {
  USER: "bo_user",
  CREDENTIAL: "bo_credential",
  PERMISSIONS: "bo_permissions",
  PERMISSIONS_TIMESTAMP: "bo_permissions_timestamp",
};

/**
 * Cache duration for permissions (in milliseconds)
 * Permissions are cached for 5 minutes to avoid unnecessary API calls
 */
const PERMISSIONS_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Check if a path matches an allowed pattern
 * Supports wildcards like "/social/*"
 */
function matchesPattern(pattern: string, path: string): boolean {
  // Wildcard for everything
  if (pattern === "*") return true;

  // Exact match
  if (pattern === path) return true;

  // Wildcard match (e.g., "/social/*" matches "/social/feed")
  if (pattern.endsWith("/*")) {
    const prefix = pattern.slice(0, -1); // "/social/*" → "/social/"
    const base = pattern.slice(0, -2); // "/social/*" → "/social"
    return path.startsWith(prefix) || path === base;
  }

  return false;
}

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<GoogleUser | null>(null);
  const [permissions, setPermissions] = useState<UserPermissions | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch permissions from backend
   */
  const fetchPermissions = useCallback(
    async (email: string, name: string): Promise<UserPermissions | null> => {
      try {
        const response = await mainApi.post<UserPermissions>(
          "/bo/admin/my-permissions",
          { email, name }
        );
        return response;
      } catch (err) {
        console.error("Failed to fetch permissions:", err);
        return null;
      }
    },
    []
  );

  /**
   * Initialize auth state from localStorage on mount
   */
  useEffect(() => {
    const initAuth = async () => {
      setIsLoading(true);
      try {
        const savedUser = localStorage.getItem(STORAGE_KEYS.USER);
        const savedCredential = localStorage.getItem(STORAGE_KEYS.CREDENTIAL);
        const savedPermissions = localStorage.getItem(STORAGE_KEYS.PERMISSIONS);
        const savedTimestamp = localStorage.getItem(
          STORAGE_KEYS.PERMISSIONS_TIMESTAMP
        );

        if (savedUser && savedCredential) {
          const parsedUser = JSON.parse(savedUser) as GoogleUser;
          setUser(parsedUser);

          // Check if cached permissions are still valid
          const isPermissionsCacheValid =
            savedPermissions &&
            savedTimestamp &&
            Date.now() - parseInt(savedTimestamp, 10) <
              PERMISSIONS_CACHE_DURATION;

          if (isPermissionsCacheValid) {
            // Use cached permissions
            setPermissions(JSON.parse(savedPermissions));
            setIsLoading(false);
            return;
          }

          // If cache is stale or missing, use it temporarily for fast UI
          if (savedPermissions) {
            setPermissions(JSON.parse(savedPermissions));
          }

          // Then fetch fresh permissions in background
          const freshPermissions = await fetchPermissions(
            parsedUser.email,
            parsedUser.name
          );
          if (freshPermissions) {
            setPermissions(freshPermissions);
            localStorage.setItem(
              STORAGE_KEYS.PERMISSIONS,
              JSON.stringify(freshPermissions)
            );
            localStorage.setItem(
              STORAGE_KEYS.PERMISSIONS_TIMESTAMP,
              Date.now().toString()
            );
          }
        }
      } catch (err) {
        console.error("Error initializing auth:", err);
        // Clear invalid data
        localStorage.removeItem(STORAGE_KEYS.USER);
        localStorage.removeItem(STORAGE_KEYS.CREDENTIAL);
        localStorage.removeItem(STORAGE_KEYS.PERMISSIONS);
        localStorage.removeItem(STORAGE_KEYS.PERMISSIONS_TIMESTAMP);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, [fetchPermissions]);

  /**
   * Handle Google login
   */
  const login = useCallback(
    async (credential: string) => {
      setIsLoading(true);
      setError(null);

      try {
        // Decode Google JWT to get user info
        const decoded = jwtDecode<GoogleUser>(credential);

        // Save to state
        setUser(decoded);

        // Save to localStorage
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(decoded));
        localStorage.setItem(STORAGE_KEYS.CREDENTIAL, credential);

        // Fetch permissions from backend (auto-creates user if new)
        const userPermissions = await fetchPermissions(
          decoded.email,
          decoded.name
        );

        if (userPermissions) {
          setPermissions(userPermissions);
          localStorage.setItem(
            STORAGE_KEYS.PERMISSIONS,
            JSON.stringify(userPermissions)
          );
          localStorage.setItem(
            STORAGE_KEYS.PERMISSIONS_TIMESTAMP,
            Date.now().toString()
          );
        } else {
          // If we couldn't fetch permissions, set default member permissions
          const defaultPermissions: UserPermissions = {
            email: decoded.email,
            name: decoded.name,
            role: "member",
            role_name: "Team Member",
            allowed_pages: ["/", "/chat", "/social/*", "/ai-usage/*"],
          };
          setPermissions(defaultPermissions);
          localStorage.setItem(
            STORAGE_KEYS.PERMISSIONS,
            JSON.stringify(defaultPermissions)
          );
        }
      } catch (err) {
        console.error("Login failed:", err);
        setError("Login failed. Please try again.");
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [fetchPermissions]
  );

  /**
   * Handle logout
   */
  const logout = useCallback(() => {
    googleLogout();
    setUser(null);
    setPermissions(null);
    setError(null);
    localStorage.removeItem(STORAGE_KEYS.USER);
    localStorage.removeItem(STORAGE_KEYS.CREDENTIAL);
    localStorage.removeItem(STORAGE_KEYS.PERMISSIONS);
    localStorage.removeItem(STORAGE_KEYS.PERMISSIONS_TIMESTAMP);
  }, []);

  /**
   * Manually refresh permissions from backend
   * Useful after role changes or when admin needs to see updated permissions
   */
  const refreshPermissions = useCallback(async () => {
    if (!user) return;

    try {
      const freshPermissions = await fetchPermissions(user.email, user.name);
      if (freshPermissions) {
        setPermissions(freshPermissions);
        localStorage.setItem(
          STORAGE_KEYS.PERMISSIONS,
          JSON.stringify(freshPermissions)
        );
        localStorage.setItem(
          STORAGE_KEYS.PERMISSIONS_TIMESTAMP,
          Date.now().toString()
        );
      }
    } catch (err) {
      console.error("Failed to refresh permissions:", err);
    }
  }, [user, fetchPermissions]);

  /**
   * Check if user can access a specific page
   */
  const hasPageAccess = useCallback(
    (path: string): boolean => {
      if (!permissions) return false;

      return permissions.allowed_pages.some((pattern) =>
        matchesPattern(pattern, path)
      );
    },
    [permissions]
  );

  /**
   * Check if user has a specific role
   */
  const hasRole = useCallback(
    (role: string): boolean => {
      return permissions?.role === role;
    },
    [permissions]
  );

  /**
   * Check if user is admin
   */
  const isAdmin = permissions?.role === "admin";

  const value: AuthContextType = {
    user,
    permissions,
    isLoading,
    isAdmin,
    hasPageAccess,
    hasRole,
    login,
    logout,
    refreshPermissions,
    error,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Hook to access auth context
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export default AuthContext;
